import React, { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { generateDrilldownSql, executeRawQuery } from '../services/api';
import { substituteVars } from '../utils/sqlVariables';
import ResultsTable from './ResultsTable';

function DrillDownModal({ isOpen, onClose, originalSql, clickContext, templateVars }) {
  const [sql, setSql] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);

  const runQuery = useCallback(async (querySql) => {
    setRunning(true);
    setError(null);
    try {
      const resolved = substituteVars(querySql, templateVars);
      const data = await executeRawQuery(resolved);
      setResults(data);
    } catch (err) {
      setError(err.message || 'Query failed');
      setResults(null);
    } finally {
      setRunning(false);
    }
  }, [templateVars]);

  useEffect(() => {
    if (!isOpen || !clickContext) return;

    setSql('');
    setResults(null);
    setError(null);
    setGenerating(true);

    generateDrilldownSql(originalSql, clickContext)
      .then(data => {
        setSql(data.sql);
        setGenerating(false);
        return runQuery(data.sql);
      })
      .catch(err => {
        setError(err.message || 'Failed to generate drill-down query');
        setGenerating(false);
      });
  }, [isOpen, originalSql, clickContext, runQuery]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Drill Down: {clickContext?.labelColumn} = {clickContext?.labelValue}
            {clickContext?.groupValue && ` | ${clickContext.groupColumn} = ${clickContext.groupValue}`}
          </h3>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="modal-field">
            <label>Drill-Down SQL (editable)</label>
            {generating ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                AI is generating drill-down query
                <span className="bouncing-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            ) : (
              <div className="monaco-wrapper" style={{ height: '150px' }}>
                <Editor
                  height="150px"
                  language="sql"
                  theme="vs"
                  value={sql}
                  onChange={v => setSql(v || '')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    wordWrap: 'on',
                    fixedOverflowWidgets: true,
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <button
              onClick={() => runQuery(sql)}
              disabled={running || generating || !sql.trim()}
              className="modal-action-btn modal-action-btn-primary"
            >
              {running ? 'Running...' : 'Re-run Query'}
            </button>
          </div>

          {error && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{error}</div>}

          <div className="drilldown-results">
            {running && !results && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                Running query...
              </div>
            )}
            {results && (
              <ResultsTable results={results} defaultViewMode="table" hideViewToggle showRowIndex showSearch />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DrillDownModal;
